import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../Store/hooks';
import { getCoachInfo } from '../../Store/StudentSlices/getCoachSlice';
import { getGlobalSearchResults, resetGlobalSearch } from '../../Store/StudentSlices/globalSearchSlice';

const Header = () => {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

   const { coachInfo, loading } = useAppSelector((state: any) => state.getCoach);
  const {
    data: searchResults,
    loading: isSearchLoading,
    error: isSearchError,
    errorMessage: searchErrorMessage,
    query: searchQuery,
  } = useAppSelector((state: any) => state.globalSearch);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    dispatch(getCoachInfo());
  }, [dispatch]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsSearchFocused(true);
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      dispatch(getGlobalSearchResults(searchInput));
    }, 350);
    return () => window.clearTimeout(timeoutId);
  }, [dispatch, searchInput, searchQuery]);


  const title = pathname === '/' || pathname.startsWith('/home')
    ? 'Dashboard'
    : pathname.startsWith('/sessions')
      ? 'Sessions'
      : pathname.startsWith('/messages')
        ? 'Messages'
        : pathname.startsWith('/resources')
          ? 'Resources'
          : pathname.startsWith('/settings')
            ? 'Settings'
            : 'My Students';



  const hasQuery = searchInput.trim().length > 0;
  const hasResults =
    (searchResults?.students?.length ?? 0) > 0 ||
    (searchResults?.sessions?.length ?? 0) > 0;
  const isSearchOpen = isSearchFocused && hasQuery;

  const studentResults = useMemo(
    () => searchResults?.students ?? [],
    [searchResults?.students]
  );
  const sessionResults = useMemo(
    () => searchResults?.sessions ?? [],
    [searchResults?.sessions]
  );

  const handleStudentClick = (student: any) => {
    navigate(`/students/${student.user_id}`);
    setSearchInput('');
    setIsSearchFocused(false);
    dispatch(resetGlobalSearch());
  };

  const handleSessionClick = (session: any) => {
    navigate('/sessions');
    setSearchInput('');
    setIsSearchFocused(false);
    dispatch(resetGlobalSearch());
  };

  console.log('Header:', coachInfo, loading);

  return (
    <header className="w-full px-6 py-2 mb-3 flex items-center justify-between">
      <div className="flex items-center ">
        <h1 className="text-[16px] font-semibold text-gray-900">{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex items-center gap-2 w-[320px] h-10 px-3 border border-[#F2F4F7] rounded-[8px] bg-white shadow-sm">
          <span className="inline-flex items-center justify-center" aria-hidden>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.5 18C14.6421 18 18 14.6421 18 10.5C18 6.35786 14.6421 3 10.5 3C6.35786 3 3 6.35786 3 10.5C3 14.6421 6.35786 18 10.5 18Z"
                stroke="#98A2B3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M21 21L16.65 16.65"
                stroke="#98A2B3"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>

          <input
            aria-label="Search"
            placeholder="Search"
            className="w-full border-0 bg-transparent outline-none text-sm text-gray-900 placeholder:text-gray-400"
            value={searchInput}
            ref={searchInputRef}
            onChange={(event) => {
              setSearchInput(event.target.value);
            }}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
          />

          <span className="whitespace-nowrap text-xs text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-1.5 rounded-lg leading-none">
            Ctrl / ⌘ + K
          </span>

          {isSearchOpen ? (
            <div
              className="absolute left-0 top-12 w-full overflow-hidden rounded-[12px] border border-slate-100 bg-white shadow-lg"
              onMouseDown={(event) => event.preventDefault()}
            >
              <div className="max-h-[360px] overflow-auto">
                {isSearchLoading ? (
                  <div className="px-4 py-4 text-sm text-slate-400">
                    Searching...
                  </div>
                ) : isSearchError ? (
                  <div className="px-4 py-4 text-sm text-rose-500">
                    {searchErrorMessage || 'Failed to search.'}
                  </div>
                ) : hasResults ? (
                  <>
                    {studentResults.length > 0 ? (
                      <div className="border-b border-slate-100">
                        <p className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                          Students
                        </p>
                        <div className="pb-2">
                          {studentResults.map((student: any) => (
                            <button
                              key={`${student.user_id}-${student.name}`}
                              type="button"
                              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                              onClick={() => handleStudentClick(student)}
                            >
                              <div className="h-8 w-8 overflow-hidden rounded-full bg-slate-100">
                                {student.avatar ? (
                                  <img
                                    src={student.avatar}
                                    alt={student.name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : null}
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-semibold text-slate-900">
                                  {student.name}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {student.focus_area || student.goal || 'Student'}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}

                    {sessionResults.length > 0 ? (
                      <div>
                        <p className="px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                          Sessions
                        </p>
                        <div className="pb-2">
                          {sessionResults.map((session: any) => (
                            <button
                              key={session.id}
                              type="button"
                              className="flex w-full items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                              onClick={() => handleSessionClick(session)}
                            >
                              <div className="h-8 w-8 overflow-hidden rounded-full bg-slate-100">
                                {session.student_avatar ? (
                                  <img
                                    src={session.student_avatar}
                                    alt={session.student_name || 'Student'}
                                    className="h-full w-full object-cover"
                                  />
                                ) : null}
                              </div>
                              <div className="text-left">
                                <p className="text-sm font-semibold text-slate-900">
                                  {session.title || 'Session'}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {session.student_name || 'Student'} ·{' '}
                                  {session.time || session.status || ''}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="px-4 py-4 text-sm text-slate-400">
                    No results found.
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          aria-label="Calendar"
          className="h-10 w-10 inline-flex items-center justify-center cursor-pointer hover:bg-slate-50 rounded-lg transition"
          onClick={() => navigate('/sessions?view=calendar')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 2V5" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 2V5" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 9H21" stroke="#667085" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path
              d="M21 8.5V19C21 21 20 22 18 22H6C4 22 3 21 3 19V8.5C3 6.5 4 5.5 6 5.5H18C20 5.5 21 6.5 21 8.5Z"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* <button
          type="button"
          aria-label="Notifications"
          className="relative h-10 w-10 inline-flex items-center justify-center "
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
              stroke="#667085"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <span className="absolute top-2.5 right-2.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" aria-hidden />
        </button> */}
      </div>
    </header>
  )
}

export default Header
